import { useContext, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Form,
  MenuItem,
  TextButton,
  Typography,
  CircularProgress,
  Autocomplete,
  MenuList,
  TextField,
  styled,
  classes,
} from 'bonfire-kindling';
import Joi from 'joi';
import moment from 'moment-timezone';

import { useApp } from 'providers/AppProvider';
import { USERS_ME, TIMEZONES } from 'constants/apis';
import { createFormValidator } from 'helpers/validation';
import { ToastContext } from 'contexts';
import axios from 'helpers/axios';
import i18n from '../../i18n';

const schema = Joi.object({
  timezone: Joi.string()
    .allow(null)
    .required()
    .messages({
      'any.required': i18n.t('form_errors.required', {
        field: i18n.t('settings.timezone.autocomplete_label'),
      }),
    }),
});

const validateForm = createFormValidator(schema);

export const formatTzLabel = (tz, { isDefault = false } = {}) => {
  if (isDefault) {
    return { label: i18n.t('settings.timezone.autocomplete_default_option'), offset: -Infinity };
  }

  const now = moment().tz(tz);
  const offset = now.utcOffset();

  const min = offset;
  const hr = (min / 60) ^ 0;
  const label = `(GMT ${hr < 0 ? `- ${Math.abs(hr)}` : `+ ${hr}`}) ${tz}`;

  return { label, offset };
};

const createTzOption = (tz, { isDefault = false } = {}) => {
  const { label, offset } = formatTzLabel(tz, { isDefault });
  const value = tz;

  return { label, value, offset };
};

const StyledMenuList = styled(MenuList)(({ theme }) => ({
  '& li:active': {
    backgroundColor: 'rgba(0,0,0,.1)',
  },
  "& li[data-focus='true']": {
    backgroundColor: 'rgba(0,0,0,.1)',
  },
  backgroundColor: theme.palette.background.paper,
  border: '1px solid rgba(0,0,0,.25)',
  width: '100%',
  maxHeight: '12rem',
  overflow: 'auto',
  position: 'absolute',
  zIndex: 1,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  [`& .${classes.components.MuiInput.root}`]: {
    paddingBottom: 0,
    [`& .${classes.components.MuiInput.input}`]: {
      padding: `0.4rem ${theme.spacing(2)}`,
    },
  },
}));

const TimezoneSettings = () => {
  const { t } = useTranslation();
  const { timezone: vendorTz, language, onTimezoneUpdated } = useApp();
  const { triggerToast } = useContext(ToastContext);

  const initialValues = useMemo(
    () => ({ timezone: createTzOption(vendorTz, { isDefault: vendorTz === null }) }),
    [vendorTz]
  );

  const {
    isLoading,
    isError,
    data: timezones,
  } = useQuery(['timezones'], () => axios.get(TIMEZONES).then(({ data }) => data), {
    staleTime: Infinity,
    onError: () => triggerToast({ message: t('generic_error') }),
  });

  const { mutateAsync } = useMutation(payload => axios.patch(USERS_ME, payload));

  const options = useMemo(() => {
    const defaultOpt = createTzOption(null, { isDefault: true });
    const opts = (timezones ?? []).map(tz => createTzOption(tz));

    // Avoid `.unshift()` since it's expensive.
    opts.push(defaultOpt);

    return opts.sort((a, b) => a.offset - b.offset);
  }, [timezones]);

  return (
    <>
      <Typography variant="h1" component="h2" style={{ mb: 2 }}>
        {t('settings.timezone.title')}
      </Typography>
      {isLoading ? (
        <Box sx={{ mt: 5 }}>
          <CircularProgress
            aria-label={t('components.loading_icon')}
            sx={{ display: 'block', margin: 'auto' }}
          />
        </Box>
      ) : (
        <Box sx={{ maxWidth: '25rem' }}>
          <Form
            onSubmit={({ timezone }) => {
              const formValues = { timezone: timezone?.value };
              const errors = validateForm(formValues);

              const payload = { ...formValues, language };
              return (
                errors ??
                mutateAsync(payload, {
                  onError: () => triggerToast({ message: t('generic_error') }),
                  onSuccess: ({ data }) => {
                    onTimezoneUpdated(data.timezone);
                    triggerToast({
                      message: t('settings.save_success'),
                      variant: 'success',
                    });
                  },
                })
              );
            }}
            initialValues={initialValues}
            disabled={isError}
          >
            {({ handleSubmit, submitting }) => (
              <form onSubmit={handleSubmit}>
                <Form.Field name="timezone">
                  {({ input, meta }) => (
                    <Autocomplete
                      sx={{ mt: 3 }}
                      options={options}
                      value={input.value || null}
                      onChange={(_, option) => input.onChange(option || null)}
                      isOptionEqualToValue={(option, selectedOption) =>
                        option.value === selectedOption?.value
                      }
                      ListboxComponent={StyledMenuList}
                      renderOption={(props, option) => (
                        <MenuItem {...props} label={option.label} value={option.value} />
                      )}
                      renderInput={params => (
                        <StyledTextField
                          {...params}
                          maxLength={64}
                          fullWidth
                          label={t('settings.timezone.autocomplete_label')}
                          error={meta.submitError && !meta.dirtySinceLastSubmit}
                          helperText={
                            meta.touched && meta.submitError && !meta.dirtySinceLastSubmit
                              ? meta.submitError
                              : ''
                          }
                        />
                      )}
                    />
                  )}
                </Form.Field>
                <TextButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  label={t('settings.timezone.save')}
                  sx={{ mt: 4 }}
                  isBusy={submitting}
                  disabled={isError}
                />
              </form>
            )}
          </Form>
        </Box>
      )}
    </>
  );
};

export default TimezoneSettings;
